import emojiRegex from 'emoji-regex';
import { isEmpty, isNil } from 'lodash';
import { API } from '@shared/types/api';

/**
 * Matches MBChannelImage's check: treats null, undefined, and "" all as "no image"
 */
function hasImageUrl(imageUrl: string | null | undefined): boolean {
  return !isNil(imageUrl) && !isEmpty(imageUrl);
}

/**
 * Low-level helper: Extract emoji from FIRST POSITION of a string
 * Only returns an emoji if it's the very first character
 */
export function getEmojiFromString(value: string): string {
  if (!value) {
    return '';
  }
  const match = emojiRegex().exec(value);
  // Only return the emoji if it's at position 0
  if (match && match.index === 0) {
    return match[0];
  }
  return '';
}

/**
 * Low-level helper: Remove ONLY the first emoji from a string
 * Used when the first emoji is being used as the channel image
 */
export function removeFirstEmoji(name: string): string {
  const emoji = getEmojiFromString(name);
  if (!emoji) {
    return name;
  }
  // Remove the first emoji and trim any leading whitespace
  return name.slice(emoji.length).trimStart();
}

/**
 * Low-level helper: Remove ALL emojis from a string
 * Used for workflow template names where we want a clean entry without any emojis
 */
export function getNameWithoutEmoji(name: string): string {
  return name.replace(emojiRegex(), '').trimStart();
}

/**
 * CENTRALIZED LOGIC: Parse channel data from database
 *
 * When loading a channel from the database, we need to determine:
 * - What goes in the name field
 * - What goes in the emoji field (for use as image)
 *
 * Rules:
 * - If channel has an imageUrl: name stays as-is, no emoji extracted
 * - If no imageUrl: extract first emoji (for use as image), remove it from name input
 */
export function parseChannelForEditing(channel?: Pick<API.ChannelFragment, 'name' | 'imageUrl'>): {
  name: string;
  emoji: string;
} {
  const channeName = channel?.name ?? '';

  // If there's an imageUrl, the emoji should stay in the name
  if (hasImageUrl(channel?.imageUrl)) {
    return {
      name: channeName,
      emoji: '',
    };
  }

  // No imageUrl: emoji will be used as channel image, remove it from name input
  const emoji = getEmojiFromString(channeName);
  const name = emoji ? removeFirstEmoji(channeName) : channeName;
  return {
    name: name,
    emoji: emoji,
  };
}

/**
 * CENTRALIZED LOGIC: Format channel name for database
 *
 * When saving a channel, we need to determine what to save in the name field.
 *
 * Rules:
 * - If there's an imageUrl: save name as-is (emoji is just part of the name)
 * - If no imageUrl but there's an emoji: prepend emoji to name for database
 */
export function formatChannelNameForSaving(
  name: string,
  emoji: string,
  imageUrl: string | null | undefined,
): string {
  // If there's an imageUrl, save the name as-is
  if (hasImageUrl(imageUrl)) {
    return name;
  }

  // No imageUrl: prepend emoji to name for database storage
  if (emoji) {
    return emoji + ' ' + name;
  }

  return name;
}

/**
 * CENTRALIZED LOGIC: Format channel name for display
 *
 * When displaying a channel name, we need to determine whether to strip the emoji.
 *
 * Rules:
 * - If there's an imageUrl: show name as-is (emoji is part of the name)
 * - If no imageUrl: strip ONLY FIRST emoji (it's being shown as the channel image)
 */
export function formatChannelNameForDisplay(channel: {
  name: string;
  imageUrl?: string | null | undefined;
}): string {
  // If there's an imageUrl, show the name as-is
  if (hasImageUrl(channel.imageUrl)) {
    return channel.name;
  }

  // No imageUrl: strip only the first emoji since it's shown as the image
  // Keep all other emojis in the name
  return removeFirstEmoji(channel.name);
}
